import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { FaYoutube, FaTwitch, FaTiktok } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { getYoutubeAuthUrl, getTwitchAuthUrl } from '@/lib/utils/authUrls';
import { isDirectAuthMode } from '@/lib/config/apiFlags';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/pages/Login/
 * 소셜 로그인 페이지 - Passport.js 기반 OAuth 리다이렉트
 */

/** Dev Auth Guide 다이얼로그를 통해 로그인하는 호스트 */
const DEV_AUTH_HOSTS = [
  'localhost',
  'influencer2.ggcontent.com',
];

function useLoginMode(): 'normal' | 'direct' | 'dev-guide' {
  if (isDirectAuthMode()) return 'direct';
  const host = window.location.hostname;
  if (DEV_AUTH_HOSTS.some((h) => host === h)) return 'dev-guide';
  return 'normal';
}

const ERROR_TEXTS: Record<string, string> = {
  auth_denied: 'Authorization denied',
  not_enough_subs: "It doesn't look like your channel meets our requirements yet. Please work on having at least 1k subscribers before requesting to join a campaign.",
  not_enough_twitter_followers: "It doesn't look like your account meets our requirements yet. Please work on having at least 5k followers before requesting to join a campaign.",
  not_enough_twitch_followers: "It doesn't look like your account meets our requirements yet. Please work on having at least 1k followers before requesting to join a campaign.",
  not_enough_youtube_permission: 'It seems that you have not granted us the required permissions to access your YouTube account. Please try logging in again.',
  broken_email_address: "Please sign up with a proper email address that we'll be able to contact you with.",
  maintenance: 'System Maintenance. Please contact us through email for campaign discussions or inquiries.',
  default: "Can't authorize",
};

interface LoginButtonProps {
  icon: React.ReactNode;
  text: string;
  href: string;
  bgColor: string;
  hoverColor: string;
  disabled?: boolean;
  /** 'normal': 기본 동작, 'direct': 백엔드 직접 호출, 'dev-guide': 다이얼로그 열기 */
  mode?: 'normal' | 'direct' | 'dev-guide';
}

function LoginButton({ icon, text, href, bgColor, disabled, mode = 'normal' }: LoginButtonProps) {
  const content = (
    <div className="grid grid-cols-[2rem_1fr] items-center gap-3 w-full max-w-[260px] mx-auto">
      <span className="text-2xl justify-self-center">{icon}</span>
      <span className="text-left">
        {text}
        {mode === 'dev-guide' && (
          <span className="ml-2 text-xs bg-white/25 px-1.5 py-0.5 rounded">Dev</span>
        )}
      </span>
    </div>
  );

  if (disabled) {
    return (
      <div
        className="w-full py-3.5 px-6 rounded-xl text-white/50 cursor-not-allowed mb-3 text-lg font-medium"
        style={{ backgroundColor: `${bgColor}60` }}
      >
        {content}
      </div>
    );
  }

  const handleDevGuideClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('open-dev-auth-guide'));
  };

  return (
    <a
      href={mode === 'dev-guide' ? '#' : href}
      onClick={mode === 'dev-guide' ? handleDevGuideClick : undefined}
      className="block w-full py-3.5 px-6 rounded-xl text-white mb-3 text-lg font-medium transition-colors hover:opacity-90"
      style={{ backgroundColor: bgColor }}
    >
      {content}
    </a>
  );
}

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const loginMode = useLoginMode();

  const error = searchParams.get('error');
  const errorMessage = searchParams.get('message');

  useEffect(() => {
    if (currentUser) {
      navigate('/my-campaigns', { replace: true });
    }
  }, [currentUser, navigate]);

  const errorText = errorMessage || (error && (ERROR_TEXTS[error] || ERROR_TEXTS.default));

  // direct: 백엔드 OAuth URL, 그 외: same-origin 상대 경로
  const youtubeHref = getYoutubeAuthUrl({ direct: loginMode === 'direct' });
  const twitchHref = getTwitchAuthUrl({ direct: loginMode === 'direct' });

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-lg p-12">
        <h2 className="text-3xl font-bold text-center mb-8">Welcome Back!</h2>

        {errorText && (
          <div className="flex items-start gap-3 p-4 mb-6 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-500 text-lg mt-0.5">⚠</span>
            <span className="text-sm text-red-700">{errorText}</span>
          </div>
        )}
        <div className="max-w-md mx-auto">
          <LoginButton
            icon={<FaYoutube />}
            text="Login with YouTube"
            href={youtubeHref}
            bgColor="#FF0000"
            hoverColor="#CC0000"
            mode={loginMode}
          />

          <LoginButton
            icon={<FaTwitch />}
            text="Login with Twitch"
            href={twitchHref}
            bgColor="#6441A4"
            hoverColor="#553499"
            mode={loginMode}
          />

          <LoginButton
            icon={<FaXTwitter />}
            text="Login with X"
            href="/auth/twitter/"
            bgColor="#000000"
            hoverColor="#333333"
            disabled
          />

          <LoginButton
            icon={<FaTiktok />}
            text="Login with TikTok"
            href=""
            bgColor="#010101"
            hoverColor="#333333"
            disabled
          />
        </div>
        <div className="flex flex-col gap-2 mt-10 text-xs text-gray-500 text-center leading-relaxed">
          <div>
            You reserve the right to revoke authentication at anytime via the{' '}
            <a
              href="https://security.google.com/settings/security/permissions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:underline"
            >
              Google security settings page
            </a>
            .
          </div>
          <div>
            By using our platform, you agree to our{' '}
            <a
              href="/terms_and_conditions/influencer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:underline"
            >
              Terms of Service
            </a>
            {' '}and acknowledge our{' '}
            <a
              href="/terms_and_conditions/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
